import React from 'react';
import { Check } from 'lucide-react';

/**
 * Parses text containing '**' into React elements with <strong> tags.
 */
export function parseBoldText(text) {
  if (!text) return '';
  const parts = text.split('**');
  return parts.flatMap((part, i) => {
    if (i % 2 === 1) {
      return [<strong key={`bold-${i}`} className="font-bold text-[#1E352F]">{part}</strong>];
    }
    return [parsePhoneNumbers(part, i)];
  });
}

/**
 * Helper to parse phone numbers and turn them into clickable tel: links.
 */
function parsePhoneNumbers(text, parentKey) {
  if (typeof text !== 'string') return text;
  
  // Match "8663 9393", "86639393", "86 63 93 93"
  const phoneRegex = /(86\s*63\s*93\s*93|86639393)/g;
  const parts = text.split(phoneRegex);
  
  if (parts.length === 1) return text;
  
  return parts.map((part, index) => {
    // We need to test if the part itself is a phone number
    if (part.match(/^86\s*63\s*93\s*93$/) || part === '86639393') {
      const cleanNumber = part.replace(/\s+/g, '');
      return (
        <a 
          key={`phone-${parentKey}-${index}`} 
          href={`tel:${cleanNumber}`} 
          className="text-[#eebb53] hover:text-[#dca63d] hover:underline font-semibold transition-colors"
        >
          {part}
        </a>
      );
    }
    return part;
  });
}

/**
 * Splits text by double line breaks, converts '##' headings to styled h3 elements,
 * parses '**' bold markers, and converts lines starting with '-' or '–' into list items with a Check icon.
 */
export function renderFormattedText(text, h3ClassName = "font-serif text-2xl font-bold text-[#1E352F] pt-6 pb-2") {
  if (!text) return null;
  return text.split('\n\n').map((para, index) => {
    // Handle headings (any level of markdown heading starting with #)
    if (para.startsWith('#')) {
      const headingText = para.replace(/^#+\s*/, '');
      return (
        <h3 key={index} className={h3ClassName}>
          {parseBoldText(headingText)}
        </h3>
      );
    }

    // Check if the paragraph contains bullet points (lines starting with - or – followed by space)
    const lines = para.split('\n');
    const hasBullets = lines.some(line => /^\s*[-–]\s+/.test(line));

    if (hasBullets) {
      return (
        <ul key={index} className="space-y-2.5 my-4 pl-1">
          {lines.map((line, lIdx) => {
            const match = line.match(/^\s*[-–]\s+(.*)$/);
            if (match) {
              return (
                <li key={lIdx} className="flex items-start gap-3 text-sm sm:text-base text-[#1E352F]/80 leading-relaxed">
                  <Check className="w-4 h-4 text-[#eebb53] shrink-0 mt-1" />
                  <span className="flex-1">{parseBoldText(match[1])}</span>
                </li>
              );
            }
            // If there's a line in the same block that doesn't start with a bullet, render it as a paragraph
            return (
              <p key={lIdx} className="text-sm sm:text-base text-[#1E352F]/80 leading-relaxed">
                {parseBoldText(line)}
              </p>
            );
          })}
        </ul>
      );
    }

    // Normal paragraph
    return (
      <p key={index} className="whitespace-pre-line text-sm sm:text-base text-[#1E352F]/80 leading-relaxed">
        {parseBoldText(para)}
      </p>
    );
  });
}
